<template>
  <div class="viewport viewport-compact"
       :class="{ 'viewport-with-top-nav': !$route.query.webInject }"
  >

    <TopNav v-if="!$route.query.webInject"></TopNav>

    <h3 class="mt-2">
      {{ $t('DealerApplicationPage.title') }} {{$route.query.partner}}
    </h3>

    <div class="mb-3">
      <template>
        <validation-observer ref="inputcheck">
          <InputField :inputClass="'form-control-amount mt-2 small-placeholder ' + (showAmountHint ? 'is-invalid' : '')"
                      v-model="loanAmount"
                      :maxlength="12"
                      :placeholder="$t('DealerApplicationPage.placeholderForAmount')"
                      id="bdAmount"
                      inputmode="numeric"
                      step="1"
                      rules="no_decimals|required"
          >
          </InputField>
        </validation-observer>
        <div v-if="showAmountHint" class="text-danger small pe-2 me-1">
          {{ $t('DealerApplicationPage.hint', {minimum: $n(this.minimumLoanAmount, 'currencyNoDecimals'), maximum: $n(this.maximumLoanAmount, 'currencyNoDecimals')}) }}
        </div>
      </template>
    </div>
    <TransitionVerticalCollapse>
      <div v-if="!!this.activeLoanProducts.length" class="d-flex flex-column">
        <div class="mt-2 mb-2 font-weight-bolder">{{$t('DealerApplicationPage.product.description')}}</div>
        <RadioCard v-model="selectedLoanProduct"
                   :option="product"
                   :header="product.iuteSmart ? $t('DealerApplicationPage.smartProductTitle') : ''"
                   v-for="product in activeLoanProducts" class="mb-3 fade-in-up product-delay" :key="product.id"
                   v-show="product.draft"
        >
          <div class="mb-1">{{ product.selectedLoanPeriod }} {{$t('DealerApplicationPage.product.payments')}}</div>
          <div class="d-flex align-items-end">
            <h3 class="m-0 line-height-1" v-if="product.draft">{{ $n(product.draft.monthlyRepayment, 'currencyNoDecimals', 'currency') }}</h3>
            <div class="small text-muted"> / {{$tc('DealerApplicationPage.product.month', product.selectedLoanPeriod)}}</div>
          </div>
          <template v-slot:details>
            <table class="w-100">
              <tbody>
              <tr>
                <td>{{$t('DealerApplicationPage.product.period')}}</td>
                <td class="text-end fw-bold">
                  <select class="form-select w-auto ms-auto" id="loan-period-sm" v-model="product.selectedLoanPeriod" v-show="product.months.length > 1" :value="Math.max(...product.months)">
                    <option :value="index" v-for="index in product.months" :key="index">
                      {{ $tc('DealerApplicationPage.product.month', index) }}
                    </option>
                  </select>
                  <div v-show="product.months.length === 1">
                    {{product.months[0]}}
                  </div>
                </td>
              </tr>
              <template v-if="product.draft">
                <tr>
                  <td>{{$t('DealerApplicationPage.product.totalPayment')}}</td>
                  <td class="text-end fw-bold">{{ $n(product.draft.totalAmount, 'currencyNoDecimals') }}</td>
                </tr>
                <tr>
                  <td>{{$t('DealerApplicationPage.product.feeTotal')}}</td>
                  <td class="text-end fw-bold">{{ $n(product.draft.fees, 'currencyNoDecimals') }}</td>
                </tr>
                <tr v-if="showApr">
                  <td>{{$t('DealerApplicationPage.product.apr')}}</td>
                  <td class="text-end fw-bold">{{ $n(product.draft.apr, 'percent') }}%</td>
                </tr>
                <tr v-if="showEir">
                  <td>{{$t('DealerApplicationPage.product.eir')}}</td>
                  <td class="text-end fw-bold">{{ $n(product.draft.aprc, 'percent') }}%</td>
                </tr>
              </template>
              </tbody>
            </table>
          </template>
        </RadioCard>

      </div>
    </TransitionVerticalCollapse>

    <div class="flex-grow-1 d-md-none"></div>

    <div class="list-group list-group-escape-container mt-5 mt-md-2 mb-3">
      <slot name="before">
      </slot>
      <router-link :to="{ name: 'privacy'}" class="list-group-item list-group-item-action list-group-item-with-icon">
        <span>{{ $t('LoanTerms.personalDataProcessing') }}</span>
        <Icon icon-name="arrow-ios-forward-outline" icon-class="text-muted-more"></Icon>
      </router-link>
      <router-link :to="{ name: 'general-terms'}"
                   class="list-group-item list-group-item-action list-group-item-with-icon">
        <span>{{ $t('LoanTerms.termsAndConditions') }}</span>
        <Icon icon-name="arrow-ios-forward-outline" icon-class="text-muted-more"></Icon>
      </router-link>
    </div>

    <div class="custom-control custom-switch mb-3" id="pep">
      <input type="checkbox" class="custom-control-input" v-model="isNotPep" id="pep-agreementCheckbox">
      <i18n path="LoanAgreement.pep" tag="label" class="custom-control-label" for="pep-agreementCheckbox"
            id="pep-label">
        <template v-slot:action>
          <router-link id="pep-consent-link" :to="{ name: 'pep-consent' }">{{ $t('LoanAgreement.pepConsentLink') }}</router-link>
        </template>
      </i18n>
    </div>

    <p class="small text-muted">
      {{$t('DealerApplicationPage.advise')}}
    </p>

    <div class="mt-lg-5">
      <button class="btn btn-lg btn-primary w-100 mt-3 mb-3" :disabled="!isSubmitEnabled" @click="submitPalPaymentRequest">
        <transition name="fade" mode="out-in" :duration="155">
          <div key="applyPal">
            {{ $t('DealerApplicationPage.apply') }}
          </div>
        </transition>
      </button>
    </div>

    <div class="d-none flex-grow-1 d-md-flex"></div>

  </div>
</template>

<script>
import TopNav from '@shared/components/TopNav'
import InputField from '@shared/components/InputField'
import RadioCard from '@shared/components/RadioCard'
import Icon from '@shared/components/Icon'
import TransitionVerticalCollapse from '@shared/components/TransitionVerticalCollapse'
import _debounce from 'lodash/debounce'
import DebouncedRequestMixin from '../mixins/DebouncedRequestMixin'
import store from '@shared/store'

export default {
  name: 'DealerApplicationPage',
  mixins: [DebouncedRequestMixin],
  components: { TopNav, InputField, TransitionVerticalCollapse, RadioCard, Icon },
  props: ['showEir', 'showApr'],
  data () {
    return {
      loanAmount: undefined,
      partnerId: undefined,
      loanProducts: [],
      isNotPep: true,
      selectedLoanProduct: undefined
    }
  },
  async mounted () {
    this.calculateDraftDebounced = _debounce(this.calculateDraftForAllProducts, 300)
    this.calculateDraftForSelectedProductDebounced = _debounce(this.calculateDraftForSelectedProduct, 300)
    await this.getPartnerProducts()
    this.componentIsReady()
  },
  computed: {
    minimumLoanAmount() {
      return Math.min(...this.loanProducts.map(p => p.minLoanAmount))
    },
    maximumLoanAmount() {
      return Math.max(...this.loanProducts.map(p => p.maxLoanAmount))
    },
    activeLoanProducts() {
      return this.loanProducts
        .filter(p => this.loanAmount <= p.maxLoanAmount && this.loanAmount >= p.minLoanAmount)
        .sort((a, b) => b.iuteSmart - a.iuteSmart)
    },
    showAmountHint() {
      return this.loanAmount !== undefined && this.isInvalidAmount
    },
    isInvalidAmount() {
      return this.loanAmount === undefined || this.loanAmount < this.minimumLoanAmount || this.loanAmount > this.maximumLoanAmount
    },
    isSubmitEnabled() {
      return !!this.selectedLoanProduct && !this.isInvalidAmount && this.activeLoanProducts.map(p => p.id).includes(this.selectedLoanProduct.id)
    },
    isPalEligibleProductSelected() {
      return this.selectedLoanProduct !== undefined && this.selectedLoanProduct.draft !== undefined && this.selectedLoanProduct.draft.palEligible
    }
  },
  methods: {
    componentIsReady() {
      this.$watch('loanAmount', this.calculateDraftDebounced)
      this.$watch('selectedLoanProduct.selectedLoanPeriod', this.calculateDraftForSelectedProductDebounced)
      this.$watch('selectedLoanProduct.id', this.calculateDraftForSelectedProductDebounced)
    },
    async submitPalPaymentRequest() {
      const loanDraftRequest = {
        partnerId: this.partnerId,
        pointOfSaleId: this.$route.query.posId,
        productId: this.selectedLoanProduct.id,
        amount: this.loanAmount,
        periodInMonths: this.selectedLoanProduct.selectedLoanPeriod,
        pep: this.isPep
      }
      await this.$http.post(`/api/partners/loan-draft/${store.state.currentUser.pin}`, loanDraftRequest)
        .then(() => {
          this.$router.push({
            name: 'dealer-application-success',
            params: { flow: this.isPalEligibleProductSelected ? 'pal' : 'regular' }
          })
        })
        .catch(() => {
          this.$errorToast(this.$t('errors.technical.title'), this.$t('errors.technical.content'))
        })
    },
    async calculateDraftForSelectedProduct() {
      const parameters = {
        params: {
          amount: this.loanAmount,
          duration: this.selectedLoanProduct.selectedLoanPeriod,
          productId: this.selectedLoanProduct.id
        }
      }
      await this.handleLatestRequest(() => this.$http.get(`/api/partners/products/draft/${store.state.currentUser.pin}`, parameters), response => {
        this.selectedLoanProduct.draft = response.data
      })
    },
    async calculateDraftForAllProducts() {
      if (!await this.$refs.inputcheck.validate()) return
      for (const p of this.activeLoanProducts) {
        const parameters = {
          params: {
            amount: this.loanAmount,
            duration: p.selectedLoanPeriod,
            productId: p.id
          }
        }
        await this.handleLatestRequest(() => this.$http.get(`/api/partners/products/draft/${store.state.currentUser.pin}`, parameters), response => {
          this.$set(p, 'draft', response.data)
        })
      }
    },
    async getPartnerProducts() {
      const partnerId = this.$route.query.partnerId
      if (partnerId) {
        this.partnerId = partnerId
        this.loanProducts = (await this.$http.get(`/api/partners/products?partnerId=${partnerId}`)).data
          .map(p => ({ ...p, selectedLoanPeriod: Math.max(...p.months) }))
        this.selectedLoanProduct = this.loanProducts[0]
      }
    }
  }
}
</script>
<style lang="scss" scoped>
.custom-control-input {
  &:active {
     color: red;
   }

  &:focus-visible {
     outline: none;
   }

  &:hover {
    color: red;
  }

  &:focus,:focus-visible {
    color: red;
  }
  &:focus {
    &~ .custom-control-label{
      &::before {
        box-shadow: none;
      }
    }
  }
}
.product-delay:last-child {
  animation-delay: 0s;
}
</style>
